import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { X, Mail, Lock, User, ArrowLeft, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import { useAuthStore, useAppStore } from '../store';
import { T } from '../i18n';

const API_URL = process.env.REACT_APP_BACKEND_URL || (typeof window !== 'undefined' ? ((window.location.hostname === 'devapp.soviewfx.fr' || window.location.hostname === 'devadmin.soviewfx.fr') ? 'https://devhub.soviewfx.fr' : ((window.location.hostname === 'app.soviewfx.fr' || window.location.hostname === 'admin.soviewfx.fr') ? 'https://hub.soviewfx.fr' : '')) : '');

export default function AuthModal({ onClose }) {
  const { user, login, register, logout } = useAuthStore();
  const { lang } = useAppStore();
  const t = T[lang] || T.fr;
  
  const [mode, setMode] = useState('login'); // login | register | forgot | confirm_sent
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      if (mode === 'login') {
        await login(email, password);
        toast.success('Connecté !');
        onClose();
      } else if (mode === 'register') {
        await register(email, password, name, lang);
        toast.success('Compte créé ! Vérifiez votre email.');
        setMode('confirm_sent');
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };
  
  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Entrez votre email');
      return;
    }
    
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      
      if (res.ok) {
        toast.success('Si cet email existe, un lien de réinitialisation a été envoyé');
        setMode('login');
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Erreur');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };
  
  const handleResendConfirmation = async () => {
    setLoading(true);
    try {
      const { token } = useAuthStore.getState();
      const res = await fetch(`${API_URL}/api/auth/resend-confirmation`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include'
      });
      
      if (res.ok) {
        toast.success('Email de confirmation renvoyé !');
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Erreur');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };
  
  const handleLogout = async () => {
    await logout();
    toast.success('Déconnecté');
    onClose();
  };
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Overlay */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        className="relative glass-light rounded-2xl p-6 w-full max-w-sm"
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-1 rounded-lg hover:bg-white/10"
          data-testid="close-auth-modal"
        >
          <X size={20} className="text-muted-foreground" />
        </button>
        
        {user ? (
          // Logged in view
          <div className="text-center">
            {user.picture ? (
              <img 
                src={user.picture} 
                alt="" 
                className="w-16 h-16 rounded-full mx-auto mb-4"
              />
            ) : (
              <div className="w-16 h-16 rounded-full bg-secondary mx-auto mb-4 flex items-center justify-center text-white text-2xl font-bold">
                {user.name?.[0]?.toUpperCase()}
              </div>
            )}
            <p className="font-bold text-lg">{user.name}</p>
            <p className="text-sm text-muted-foreground">{user.email}</p>
            
            {/* Email verification status */}
            {user.email_verified === false && (
              <div className="mt-3 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                <p className="text-yellow-400 text-xs mb-2">Email non confirmé</p>
                <button
                  onClick={handleResendConfirmation}
                  disabled={loading}
                  className="text-yellow-400 text-xs underline disabled:opacity-50"
                >
                  {loading ? 'Envoi...' : 'Renvoyer le mail de confirmation'}
                </button>
              </div>
            )}
            
            <p className="text-sm text-primary mt-2">
              {user.credits} {t.credits}
            </p>
            
            <button
              onClick={handleLogout}
              className="mt-6 w-full py-3 rounded-xl bg-white/5 border border-white/10 text-red-400 font-medium"
              data-testid="logout-btn"
            >
              {t.logout}
            </button>
          </div>
        ) : mode === 'confirm_sent' ? (
          // Confirmation email sent
          <div className="text-center py-4">
            <div className="w-16 h-16 bg-secondary/20 rounded-full mx-auto mb-4 flex items-center justify-center">
              <CheckCircle size={32} className="text-secondary" />
            </div>
            <h3 className="text-lg font-bold text-white mb-2">Vérifiez votre email</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Un email de confirmation a été envoyé à<br/>
              <span className="text-white font-medium">{email}</span>
            </p>
            <p className="text-xs text-muted-foreground mb-6">
              Cliquez sur le lien dans l'email pour activer votre compte.
            </p>
            <button
              onClick={() => {
                setMode('login');
                onClose();
              }}
              className="w-full btn-primary"
            >
              Compris !
            </button>
          </div>
        ) : mode === 'forgot' ? (
          // Forgot password form
          <>
            <button
              onClick={() => setMode('login')}
              className="flex items-center gap-1 text-sm text-muted-foreground hover:text-white mb-4"
            >
              <ArrowLeft size={16} />
              Retour
            </button>
            
            <h2 className="text-xl font-bold text-secondary mb-2">Mot de passe oublié</h2>
            <p className="text-sm text-muted-foreground mb-6">
              Entrez votre email pour recevoir un lien de réinitialisation.
            </p>
            
            <form onSubmit={handleForgotPassword} className="space-y-4">
              <div className="relative">
                <Mail size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="email"
                  placeholder={t.email}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input pl-10"
                  required
                  data-testid="forgot-email-input"
                />
              </div>
              
              <button
                type="submit"
                disabled={loading}
                className="w-full btn-primary disabled:opacity-50"
                data-testid="forgot-submit-btn"
              >
                {loading ? 'Envoi...' : 'Envoyer le lien'}
              </button>
            </form>
          </>
        ) : (
          // Login/Register form
          <>
            <h2 className="text-xl font-bold text-secondary mb-6">
              {mode === 'login' ? t.login : t.register}
            </h2>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              {mode === 'register' && (
                <div className="relative">
                  <User size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <input
                    type="text"
                    placeholder={t.name}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="input pl-10"
                    required
                    data-testid="name-input"
                  />
                </div>
              )}
              
              <div className="relative">
                <Mail size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="email"
                  placeholder={t.email}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input pl-10"
                  required
                  data-testid="email-input"
                />
              </div>
              
              <div className="relative">
                <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="password"
                  placeholder={t.password}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input pl-10"
                  required
                  minLength={6}
                  data-testid="password-input"
                />
              </div>
              
              {mode === 'login' && (
                <button
                  type="button"
                  onClick={() => setMode('forgot')}
                  className="text-xs text-secondary hover:underline"
                  data-testid="forgot-password-link"
                >
                  Mot de passe oublié ?
                </button>
              )}
              
              <button
                type="submit"
                disabled={loading}
                className="w-full btn-primary disabled:opacity-50"
                data-testid="submit-auth-btn"
              >
                {loading ? '...' : mode === 'login' ? t.login : t.register}
              </button>
            </form>
            
            <button
              onClick={() => setMode(mode === 'login' ? 'register' : 'login')}
              className="w-full mt-4 text-sm text-muted-foreground hover:text-white transition-colors"
              data-testid="toggle-auth-mode"
            >
              {mode === 'login' ? t.register : t.login}
            </button>
          </>
        )}
      </motion.div>
    </div>
  );
}
